import { Modal } from '@constants/Themed';
import { Platform, View } from 'react-native';
import { useStableWindowDimensions } from '@hooks/useStableWindowDimensions';
import React, { useCallback, useEffect, useState } from 'react';
import WebView from 'react-native-webview';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { ACCEPT_TERMS, HIDE_LOADING_MODAL, HIDE_TC_MODAL, RESET_APP, DECLINE_TERMS, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import apis from '@api/api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Checkbox from 'expo-checkbox';
import moment from 'moment';
import { XStack, YStack, Button, SizableText, H3 } from 'tamagui';
import { Card } from '@components/Global/FrostedCard';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { responsivePadding } from '@stylesheet';

interface TCModalTypes {
  countryCode?: string,
  userId?: string
}

const TERMS_ACTION_ROW_GAP = responsivePadding(8);
const TERMS_ACTION_MIN_HEIGHT = responsivePadding(56);

/** Icon + label: outer shell fills button height and centers the row vertically (Tamagui Button does not stretch children by default). */
const TermsActionButtonContent = ({
  iconName,
  label,
  minHeight,
}: {
  iconName: 'close' | 'check-circle';
  label: string;
  minHeight: number;
}) => (
  <View
    style={{
      minHeight,
      width: '100%',
      alignSelf: 'stretch',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: responsivePadding(4),
      paddingHorizontal: responsivePadding(4),
    }}
  >
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        maxWidth: '100%',
        columnGap: TERMS_ACTION_ROW_GAP,
        rowGap: responsivePadding(4),
      }}
    >
      <MaterialIcons name={iconName} size={18} color="#ffffff" />
      <SizableText
        color="white"
        size="$3"
        fontWeight="600"
        textAlign="center"
        flexShrink={1}
        maxWidth="100%"
        style={
          Platform.OS === 'android'
            ? { includeFontPadding: false, textAlignVertical: 'center' as const }
            : undefined
        }
      >
        {label}
      </SizableText>
    </View>
  </View>
);

const TCModal = () => {
  const theme = themeSelector();
  const visible = useSelector((state: DefaultRootState) => state.modals.tcModal?.modalDisplayed);
  const userId = useSelector((state: DefaultRootState) => state.modals.tcModal?.userId);
  const insets = useSafeAreaInsets();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tcVersion, setTCVersion] = useState('Terms and Conditions');
  const [activeCheck, setActiveCheck] = useState(false);
  const { width, height } = useStableWindowDimensions();
  const [date, setDate] = useState(new Date().toISOString());
  const [error, setError] = useState(false);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const dispatch = useDispatch();

  const TC_URL = 'https://smartsaas.co.uk/terms-and-conditions?hideNavbar=true';

  const handleGetTerms = async () => {
    setLoading(true);
    setError(false);
    // Set a default version and date
    setTCVersion('Terms and Conditions');
    setDate(new Date().toISOString());
    setLoading(false);
  }

  const activateCheck = useCallback(() => {
    setActiveCheck(true);
  }, [loading])

  const setTC = async () => {
    await apis.updateTC(userId, tcVersion).then(res => {
      if (res.status === 200) {
        // Clear cancellation flag when terms are accepted
        AsyncStorage.removeItem('tc_cancelled').catch(e => {
          console.error('[TCModal] Error clearing cancellation flag:', e);
        });
        dispatch({ type: ACCEPT_TERMS });
        setTimeout(() => dispatch({ type: HIDE_TC_MODAL }), 100);
      }
    })
  }

  const handleCancelTermsFlow = useCallback(async () => {
    const cancellationTimestamp = Date.now();
    try {
      await AsyncStorage.setItem('tc_cancelled', JSON.stringify({ userId, timestamp: cancellationTimestamp }));
    } catch (e) {
      console.error('[TCModal] Error setting cancellation flag:', e);
    }

    dispatch({ type: DECLINE_TERMS });
    await new Promise(resolve => setTimeout(resolve, 50));
    dispatch({ type: HIDE_TC_MODAL });

    apis.cancelUserCreation(userId).catch((error) => {
      console.error('[TCModal] Error archiving user:', error);
    });

    try {
      await AsyncStorage.removeItem('userData');
    } catch (storageError) {
      console.error('[TCModal] Error clearing userData from storage:', storageError);
    }

    dispatch({ type: RESET_APP });
  }, [dispatch, userId]);

  useEffect(() => {
    handleGetTerms();
    setTimeout(() => activateCheck(), 2000)
  }, []);
  const { t } = useTranslation();
  const isCompactActions = width < 480;
  const actionBarPaddingBottom =
    Platform.OS === 'web'
      ? responsivePadding(12)
      : Math.max(insets.bottom, responsivePadding(12));
  /** Cap body height; smaller chrome reserve yields a taller WebView while staying in viewport */
  const modalChromeReserve = Platform.select({
    web: responsivePadding(44),
    default: responsivePadding(72),
  });
  const modalBodyMaxHeight = Math.max(
    responsivePadding(280),
    height -
      Math.max(insets.top, responsivePadding(8)) -
      Math.max(insets.bottom, responsivePadding(8)) -
      modalChromeReserve,
  );

  return (
    <Modal
      isVisible={visible}
      animationIn={Platform.OS === 'web' ? 'slideInUp' : 'fadeIn'}
      onBackdropPress={async () => {
        const cancellationTimestamp = Date.now();
        try {
          await AsyncStorage.setItem('tc_cancelled', JSON.stringify({ userId, timestamp: cancellationTimestamp }));
        } catch (e) {
          console.error('[TCModal] Error setting cancellation flag:', e);
        }

        // Dispatch DECLINE_TERMS
        dispatch({ type: DECLINE_TERMS });
        await new Promise(resolve => setTimeout(resolve, 50));

        // Clear user data from AsyncStorage
        try {
          await AsyncStorage.removeItem('userData');
        } catch (storageError) {
          console.error('Error clearing userData from storage:', storageError);
        }
        // Clear user data from Redux to prevent automatic navigation
        dispatch({ type: RESET_APP });
        dispatch({ type: HIDE_TC_MODAL });

        // Show auth modal with cancellation message
        setTimeout(() => {
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              content: 'User cancelled',
              type: 'info',
            },
          });
        }, 500);
      }}
      style={{ flex: 1, marginTop: Platform.select({ web: 0, default: "15%" }), minWidth: Platform.OS === 'web' ? "90vw" : '90%', minHeight: "60vh", paddingVertical: Platform.select({ web: '0%' }), top: Platform.select({native: "-5%"}) }}
      closeButtonStyle={{ top: 60 }}
      deviceWidth={width}
      hideCloseButton={true}
      onModalWillShow={() => dispatch({ type: HIDE_LOADING_MODAL })}
      deviceHeight={height}
    >
      <YStack
        flex={1}
        minHeight={0}
        maxHeight={modalBodyMaxHeight}
        width="100%"
        backgroundColor={theme ? "#ffffff" : "#0f172a"}
        padding="$2"
        gap="$2"
        minWidth={Platform.OS != 'web' ? '100%' : '80vw'}
        borderRadius={16}
      >
        {/* Header Card */}
        <Card
          flexShrink={0}
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$3"
          elevate
        >
          <XStack alignItems="center" gap="$2" marginBottom="$1">
            <View style={{
              backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
              borderRadius: 12,
              padding: responsivePadding(10)
            }}>
              <MaterialIcons
                name="description"
                size={24}
                color={theme ? "#0ea5e9" : "#38bdf8"}
              />
            </View>
            <YStack flex={1}>
              <H3
                color={theme ? "#1e293b" : "#f1f5f9"}
                fontWeight="600"
                fontSize="$5"
              >
                {t('Terms and Conditions')}
              </H3>
              <SizableText
                size="$3"
                color={theme ? "#64748b" : "#94a3b8"}
                marginTop="$1"
              >
                {t('Please review and accept to proceed')}
              </SizableText>
            </YStack>
          </XStack>
        </Card>

        {/* Terms Content Card — must shrink (minHeight 0 + flexShrink) or footer buttons overflow the modal */}
        <Card
          flex={1}
          flexShrink={1}
          minHeight={0}
          minWidth={0}
          backgroundColor={theme ? "#ffffff" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$2"
          overflow="hidden"
        >
          {!error ? (
            <WebView
              source={{ uri: TC_URL }}
              style={{
                flex: 1,
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 12
              }}
              containerStyle={{
                flex: 1,
                borderRadius: 12,
                overflow: 'hidden'
              }}
              onLoadStart={() => setLoading(true)}
              onLoadEnd={() => {
                setLoading(false);
              }}
              onError={(syntheticEvent) => {
                const { nativeEvent } = syntheticEvent;
                console.error('WebView error: ', nativeEvent);
                setError(true);
                setLoading(false);
              }}
              injectedJavaScript={`
                (function() {
                  const viewport = document.querySelector('meta[name="viewport"]');
                  const viewportContent = 'width=device-width, initial-scale=1.0, maximum-scale=1.0';
                  if (viewport) {
                    viewport.setAttribute('content', viewportContent);
                  } else {
                    const meta = document.createElement('meta');
                    meta.name = 'viewport';
                    meta.content = viewportContent;
                    document.getElementsByTagName('head')[0].appendChild(meta);
                  }
                  const bgColor = '${theme ? '#ffffff' : '#1e293b'}';
                  const textColor = '${theme ? '#1e293b' : '#f1f5f9'}';
                  const style = document.createElement('style');
                  style.textContent = 'body { background-color: ' + bgColor + ' !important; color: ' + textColor + ' !important; font-size: 14px; line-height: 1.6; padding: 16px; }';
                  document.head.appendChild(style);
                  
                  // Try to extract title from page
                  const title = document.querySelector('h1')?.textContent || document.title || 'Terms and Conditions';
                  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'title', value: title }));
                  
                  return true;
                })();
              `}
              onMessage={(event) => {
                try {
                  const data = JSON.parse(event.nativeEvent.data);
                  if (data.type === 'title') {
                    setTCVersion(data.value);
                  }
                } catch (e) {
                  // Ignore parse errors
                }
              }}
              startInLoadingState={true}
              scalesPageToFit={true}
            />
          ) : (
            <YStack
              flex={1}
              paddingVertical="$10"
              gap="$6"
              alignItems="center"
              justifyContent="center"
            >
              <MaterialIcons
                name="error-outline"
                size={48}
                color={theme ? "#ef4444" : "#f87171"}
              />
              <SizableText
                size="$4"
                color={theme ? "#1e293b" : "#f1f5f9"}
                textAlign="center"
                paddingHorizontal="$4"
              >
                  {t('Could not connect to server to download terms and conditions. Please check your network connection.')}
              </SizableText>
              <Button
                backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                color="white"
                  onPress={async () => {
                    const cancellationTimestamp = Date.now();
                    try {
                      await AsyncStorage.setItem('tc_cancelled', JSON.stringify({ userId, timestamp: cancellationTimestamp }));
                    } catch (e) {
                      console.error('[TCModal] Error setting cancellation flag:', e);
                    }

                    // Dispatch DECLINE_TERMS
                    dispatch({ type: DECLINE_TERMS });
                    await new Promise(resolve => setTimeout(resolve, 50));

                    // Clear user data from AsyncStorage
                    try {
                      await AsyncStorage.removeItem('userData');
                    } catch (storageError) {
                      console.error('Error clearing userData from storage:', storageError);
                    }
                    // Clear user data from Redux to prevent automatic navigation
                    dispatch({ type: RESET_APP });
                    dispatch({ type: HIDE_TC_MODAL });

                    // Show auth modal with cancellation message
                    setTimeout(() => {
                      dispatch({
                        type: SHOW_AUTH_MODAL,
                        data: {
                          content: 'User cancelled',
                          type: 'info',
                        },
                      });
                    }, 500);
                  }}
                borderRadius={12}
                paddingHorizontal="$4"
              >
                <SizableText color="white" size="$3" fontWeight="600">
                    {t('Return to login')}
                </SizableText>
              </Button>
            </YStack>
          )}
        </Card>

        {/* Agreement Section */}
        <Card
          flexShrink={0}
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$3"
        >
          <YStack gap="$2">
            <XStack
              width="100%"
              maxWidth="100%"
              alignItems="center"
              gap="$2"
              paddingBottom="$2"
              borderBottomWidth={1}
              borderBottomColor={theme ? "#e2e8f0" : "#334155"}
            >
              <SizableText
                color={theme ? "#1e293b" : "#f1f5f9"}
                size="$3"
                flex={1}
                adjustsFontSizeToFit
                numberOfLines={2}
              >
                {t('Check this box to agree to our terms and conditions')}
              </SizableText>
              <Checkbox
                value={checked}
                disabled={!activeCheck}
                onValueChange={setChecked}
                color={checked ? (theme ? "#0ea5e9" : "#38bdf8") : undefined}
              />
            </XStack>

            <XStack gap="$4" justifyContent="center">
              {/* <SizableText
                size="$2"
                color={theme ? "#64748b" : "#94a3b8"}
                fontStyle="italic"
              >
                Version: {tcVersion || 'N/A'}
              </SizableText> */}
              <SizableText
                size="$2"
                color={theme ? "#64748b" : "#94a3b8"}
                fontStyle="italic"
              >
                Date: {date ? moment(date).format(globalDate) : moment().format(globalDate)}
              </SizableText>
            </XStack>
          </YStack>
        </Card>

        {/* Action Buttons — full width on compact screens; equal columns with wrapping labels on wider layouts */}
        {isCompactActions ? (
          <YStack
            flexShrink={0}
            gap="$2"
            width="100%"
            maxWidth="100%"
            paddingTop="$2"
            paddingHorizontal="$1"
            paddingBottom={actionBarPaddingBottom}
          >
            <Button
              backgroundColor="#ef4444"
              color="white"
              width="100%"
              borderRadius={12}
              minHeight={TERMS_ACTION_MIN_HEIGHT}
              paddingVertical={0}
              paddingHorizontal={responsivePadding(8)}
              justifyContent="center"
              alignItems="stretch"
              onPress={handleCancelTermsFlow}
            >
              <TermsActionButtonContent
                iconName="close"
                label={t('Cancel')}
                minHeight={TERMS_ACTION_MIN_HEIGHT}
              />
            </Button>

            <Button
              backgroundColor={!checked ? '#64748b' : theme ? '#0ea5e9' : '#38bdf8'}
              color="white"
              width="100%"
              borderRadius={12}
              minHeight={TERMS_ACTION_MIN_HEIGHT}
              paddingVertical={0}
              paddingHorizontal={responsivePadding(8)}
              justifyContent="center"
              alignItems="stretch"
              disabled={!checked}
              opacity={checked ? 1 : 0.6}
              onPress={() => setTC()}
            >
              <TermsActionButtonContent
                iconName="check-circle"
                label={t('Proceed')}
                minHeight={TERMS_ACTION_MIN_HEIGHT}
              />
            </Button>
          </YStack>
        ) : (
          <XStack
            flexShrink={0}
            gap="$2"
            width="100%"
            maxWidth="100%"
            alignItems="stretch"
            paddingTop="$2"
            paddingHorizontal="$1"
            paddingBottom={actionBarPaddingBottom}
          >
            <Button
              backgroundColor="#ef4444"
              color="white"
              flex={1}
              flexBasis={0}
              flexGrow={1}
              minWidth={0}
              borderRadius={12}
              minHeight={TERMS_ACTION_MIN_HEIGHT}
              paddingVertical={0}
              paddingHorizontal={responsivePadding(6)}
              justifyContent="center"
              alignItems="stretch"
              onPress={handleCancelTermsFlow}
            >
              <TermsActionButtonContent
                iconName="close"
                label={t('Cancel')}
                minHeight={TERMS_ACTION_MIN_HEIGHT}
              />
            </Button>

            <Button
              backgroundColor={!checked ? '#64748b' : theme ? '#0ea5e9' : '#38bdf8'}
              color="white"
              flex={1}
              flexBasis={0}
              flexGrow={1}
              minWidth={0}
              borderRadius={12}
              minHeight={TERMS_ACTION_MIN_HEIGHT}
              paddingVertical={0}
              paddingHorizontal={responsivePadding(6)}
              justifyContent="center"
              alignItems="stretch"
              disabled={!checked}
              opacity={checked ? 1 : 0.6}
              onPress={() => setTC()}
            >
              <TermsActionButtonContent
                iconName="check-circle"
                label={t('Proceed')}
                minHeight={TERMS_ACTION_MIN_HEIGHT}
              />
            </Button>
          </XStack>
        )}
      </YStack>
    </Modal>
  )
}
export default TCModal; 
